import React, { useContext } from "react";
import { TableCellContext } from "./MarkdownRenderer";
import DownloadLinkBox from "../Common/DownloadLinkBox";
import StorageFileLink from "./StorageFileLink";
import URLPreview from "../Common/URLPreview";
import logger from '../../services/Logger';

const MdAnchor = ({ href, children }) => {
  const inTableCell = useContext(TableCellContext);

  // Inside a table cell — always render a plain link to avoid breaking table layout
  if (inTableCell) {
    return (
      <a
        href={href}
        className="photon-md-link"
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  }

  // Resolve storage paths through backend (STORAGE_PATH_ prefix = raw GCS path)
  if (href?.startsWith('STORAGE_PATH_')) {
    return <StorageFileLink href={href.slice(13)}>{children}</StorageFileLink>;
  }

  // Convert children to string for checking
  const childrenText = React.Children.toArray(children).join("").toLowerCase();

  // Check if this is a download link for files (notebook, csv, etc.)
  const isDownloadableFile =
    // Check href for file extensions
    (href &&
      (href.includes(".ipynb") ||
        href.includes(".csv") ||
        href.includes(".xlsx") ||
        href.includes(".pdf") ||
        href.includes("download"))) ||
    // Check children text for download keywords and file extensions
    (childrenText &&
      (childrenText.includes("download") ||
        childrenText.includes(".ipynb") ||
        childrenText.includes(".csv") ||
        childrenText.includes(".xlsx") ||
        childrenText.includes(".pdf") ||
        childrenText.includes("notebook")));

  // Check if this is a URL that should get rich preview
  const isURLForPreview = (url, text) => {
    if (!url) return false;

    // If the link text is the same as URL or very similar, it's likely a plain URL
    const urlWithoutProtocol = url.replace(/^https?:\/\//, "");
    const textString = React.Children.toArray(text).join("");

    return (
      (url.startsWith("http://") || url.startsWith("https://")) &&
      (textString === url ||
        textString === urlWithoutProtocol ||
        textString.toLowerCase().includes("http"))
    );
  };

  if (isDownloadableFile) {
    // Extract filename - prefer from children text, fallback to href
    let filename = React.Children.toArray(children).join("") || "download";

    // If children text doesn't have extension, try to get from href
    if (!filename.includes(".") && href) {
      const hrefFilename = href.split("/").pop();
      if (hrefFilename && hrefFilename.includes(".")) {
        filename = hrefFilename;
      }
    }

    // If still no extension, guess from context
    if (!filename.includes(".")) {
      if (childrenText.includes("notebook") || childrenText.includes("ipynb")) {
        filename = filename + ".ipynb";
      } else if (childrenText.includes("csv")) {
        filename = filename + ".csv";
      }
    }

    const fileExtension = filename.split(".").pop()?.toLowerCase() || "file";

    try {
      return (
        <DownloadLinkBox
          href={href}
          filename={filename}
          fileType={fileExtension}
        />
      );
    } catch (error) {
      logger.error('MdAnchor', 'DownloadLinkBox error', { error: error.message, href });
      // Fallback to regular link if component fails
      return (
        <a
          href={href}
          className="photon-md-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          {children} (Download)
        </a>
      );
    }
  }

  // Use URLPreview for plain URLs that should get rich previews
  if (isURLForPreview(href, children)) {
    return <URLPreview href={href}>{children}</URLPreview>;
  }

  return (
    <a
      href={href}
      className="photon-md-link"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
};

export default MdAnchor;
